import { FeatureCollection } from "geojson";
import { Sketch } from "ui-components";
import { create } from "zustand";

// Sample data array
const data: Sketch[] = [
	{
		id: "2d36f30c-5fa6-11ef-9ce0-02af6ed49e2d",
		cookie_code: "a738ccc6-5f64-11ef-aba4-02af6ed49e2d",
		title: "New Sketch",
		description: "This is sketch description",
		geojson: "", // Initially no GeoJSON data
		editable: true,
		created_at: "2024-08-21T17:14:39.955+07:00",
	},
	{
		id: "2d36f30c-9ce0-02af6ed49e2d",
		cookie_code: "cc6-5f64-11ef-aba4-02af6ed49e2d",
		title: "Another Sketch",
		description: "This is sketch description",
		geojson: "", // Initially no GeoJSON data
		editable: true,
		created_at: "2024-08-21T17:14:39.955+07:00",
	},
];

interface StoreState {
	sketchGeojson: FeatureCollection | null;
	drawingMode: string;
	sketches: Sketch[];
	active: string;
	selectSketch: Sketch | null;
	selectFeatureId: string;
	isNewSketch: (sketchId: string) => boolean;
	setSelectFeatureId: (featureId: string) => void;
	setDrawingMode: (mode: string) => void;
	setActive: (active: string) => void;
	setSelectSketch: (sketch: Sketch) => void;
	addSketch: (sketch: Sketch) => void;
	updateSketch: (updatedSketch: Sketch) => void;
	deleteSketch: (id: string) => void;
	deleteFeature: (featureId: string) => void;
	setSketchGeojson: (data: FeatureCollection | null) => void;
	updateSketchGeojson: (
		values: any,
		type: "style" | "icon" | "annotation",
	) => void;
}

const initialState: Omit<
	StoreState,
	| "isNewSketch"
	| "setSelectFeatureId"
	| "setDrawingMode"
	| "setActive"
	| "setSelectSketch"
	| "addSketch"
	| "updateSketch"
	| "deleteSketch"
	| "deleteFeature"
	| "setSketchGeojson"
	| "updateSketchGeojson"
> = {
	sketchGeojson: null,
	drawingMode: "",
	sketches: data,
	selectSketch: null,
	selectFeatureId: "",
	active: "Sketches",
};

export const useSketchStore = create<StoreState>((set, get) => ({
	...initialState,
	setActive: (active: string) => set({ active }),

	setSelectSketch: (sketch: Sketch) => set({ selectSketch: sketch }),

	addSketch: (sketch: Sketch) =>
		set((state) => ({
			sketches: [...state.sketches, sketch],
		})),

	setSketchGeojson: (data: FeatureCollection | null) =>
		set({ sketchGeojson: data }),

	deleteFeature: (featureId: string) =>
		set((state) => {
			// If sketchGeojson is null or undefined, return the state as it is
			if (!state.sketchGeojson) {
				return state;
			}

			return {
				sketchGeojson: {
					...state.sketchGeojson,
					type: "FeatureCollection", // Ensure type is always "FeatureCollection"
					features: state.sketchGeojson.features?.filter(
						(feature) => feature.id !== featureId,
					),
				},
			};
		}),

	setSelectFeatureId: (featureId: string) =>
		set({ selectFeatureId: featureId }),

	setDrawingMode: (mode: string) => set({ drawingMode: mode }),

	deleteSketch: (id: string) =>
		set((state) => ({
			sketches: state.sketches.filter((sketch) => sketch.id !== id),
		})),

	updateSketch: (updatedSketch: Sketch) =>
		set((state) => ({
			sketches: state.sketches.map((sketch) =>
				sketch.id === updatedSketch.id ? updatedSketch : sketch,
			),
		})),

	isNewSketch: (sketchId: string) =>
		!get().sketches.some((sketch) => sketch.id === sketchId),

	updateSketchGeojson: (values, type) =>
		set((state) => {
			const { sketchGeojson, selectFeatureId } = state;

			if (!sketchGeojson || !selectFeatureId.trim()) {
				// No geojson or no selected feature ID, return the state unchanged
				return state;
			}

			// Map over the features and update the matching one
			const updatedFeatures = sketchGeojson.features.map((feature) => {
				if (feature.id === selectFeatureId.trim()) {
					// Choose the update logic based on the type
					if (type === "style") {
						feature.properties = { ...feature.properties, style: values };
					} else if (type === "icon") {
						feature.properties = {
							...feature.properties,
							type: "marker",
							icon: {
								options: {
									iconUrl: values.iconUrl,
									iconSize: [30, 30],
									iconAnchor: [15, 30],
								},
							},
						};
					} else if (type === "annotation") {
						feature.properties = {
							...feature.properties,
							type: "annotation",
							annotation: {
								content: values.text,
							},
						};
					}
				}
				return feature;
			});

			// Create a new feature collection with the updated features
			const updatedFeatureCollection: FeatureCollection = {
				...sketchGeojson,
				features: updatedFeatures,
			};

			// Update the state with the new geojson
			return { sketchGeojson: updatedFeatureCollection };
		}),
}));

// Hooks for interacting with the store

export const useActive = () => useSketchStore((state) => state.active);
export const useSetActive = () => useSketchStore((state) => state.setActive);
export const useSetSketchGeojson = () =>
	useSketchStore((state) => state.setSketchGeojson);
export const useSetSelectSketch = () =>
	useSketchStore((state) => state.setSelectSketch);
export const useAddSketch = () => useSketchStore((state) => state.addSketch);
export const useDeleteSketch = () =>
	useSketchStore((state) => state.deleteSketch);
export const useSetDrawingMode = () =>
	useSketchStore((state) => state.setDrawingMode);
export const useDrawingMode = () =>
	useSketchStore((state) => state.drawingMode);
export const useUpdateSketch = () =>
	useSketchStore((state) => state.updateSketch);
export const useSelectSketch = () =>
	useSketchStore((state) => state.selectSketch);
export const useSketches = () => useSketchStore((state) => state.sketches);
export const useSetSelectFeatureId = () =>
	useSketchStore((state) => state.setSelectFeatureId);
export const useSelectFeatureId = () =>
	useSketchStore((state) => state.selectFeatureId);
export const useSketchGeojson = () =>
	useSketchStore((state) => state.sketchGeojson);
export const useIsNewSketch = () =>
	useSketchStore((state) => state.isNewSketch);
export const useDeleteFeature = () =>
	useSketchStore((state) => state.deleteFeature);

export const useUpdateSketchGeojson = () =>
	useSketchStore((state) => state.updateSketchGeojson);
